import { StrictMode, Component } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showToast: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', background: '#0f172a', color: '#fff',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', fontFamily: 'Cairo, sans-serif',
          direction: 'rtl', padding: '24px', textAlign: 'center',
          position: 'relative', overflow: 'hidden'
        }}>
          {/* Subtle background glow effect */}
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(248, 113, 113, 0.08) 0%, rgba(0,0,0,0) 70%)',
            pointerEvents: 'none', zIndex: 0
          }}></div>

          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '4.5rem', marginBottom: '20px', filter: 'drop-shadow(0 0 10px rgba(248, 113, 113, 0.2))' }}>⚠️</div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '16px', color: '#f87171', letterSpacing: '-0.5px' }}>
              حدث خطأ في التطبيق
            </h2>
            <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '0.98rem', maxWidth: '500px', lineHeight: '1.6' }}>
              {this.state.error?.message || 'خطأ غير معروف'}
            </p>
            <pre style={{
              background: 'rgba(30, 41, 59, 0.6)', backdropFilter: 'blur(4px)',
              padding: '20px', borderRadius: '14px', border: '1px solid rgba(248, 113, 113, 0.15)',
              fontSize: '0.8rem', color: '#f87171', maxWidth: '600px',
              overflowX: 'auto', textAlign: 'left', direction: 'ltr', marginBottom: '28px',
              fontFamily: 'Consolas, monospace', boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
            }}>
              {this.state.error?.stack?.substring(0, 500)}
            </pre>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const text = `${this.state.error?.message}\n\n${this.state.error?.stack}`;
                  navigator.clipboard.writeText(text);
                  this.setState({ showToast: true });
                  setTimeout(() => {
                    this.setState({ showToast: false });
                  }, 3000);
                }}
                style={{
                  background: 'rgba(51, 65, 85, 0.8)',
                  backdropFilter: 'blur(4px)',
                  color: '#fff', border: '1px solid rgba(255, 255, 255, 0.1)', padding: '12px 32px',
                  borderRadius: '12px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(71, 85, 105, 0.9)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(51, 65, 85, 0.8)'; }}
              >
                نسخ نص الخطأ 📋
              </button>
              <button
                onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff', border: 'none', padding: '12px 32px',
                  borderRadius: '12px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700,
                  boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)', transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(99, 102, 241, 0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.35)'; }}
              >
                إعادة تحميل التطبيق
              </button>
            </div>
          </div>

          {/* Premium Custom Toast Dialog */}
          {this.state.showToast && (
            <>
              <style>{`
                @keyframes slideUpFade {
                  0% {
                    opacity: 0;
                    transform: translate(-50%, 20px) scale(0.95);
                  }
                  100% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1);
                  }
                }
              `}</style>
              <div style={{
                position: 'fixed',
                bottom: '40px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                boxShadow: '0 10px 30px -5px rgba(16, 185, 129, 0.25), 0 0 20px rgba(16, 185, 129, 0.1)',
                borderRadius: '16px',
                padding: '14px 28px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                zIndex: 10000,
                animation: 'slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                pointerEvents: 'none',
                direction: 'rtl'
              }}>
                <span style={{ fontSize: '1.25rem' }}>✨</span>
                <span style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  تم نسخ تفاصيل الخطأ بنجاح! 📋
                </span>
              </div>
            </>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
